import React, { useState, useEffect, useMemo } from "react";
import { Plus, Search, Trash2, Shield } from "lucide-react";
import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../../components/loader/Loader";
import Modal from "../../../../components/modal/Modal";
import { Dropdown } from "../../../../components/dropdown/Dropdown";
import {
  getAllAccessZones,
  deleteAccessZone,
} from "../../../../service/securityService";
import {
  AccessZone,
  SecuritySearchFilters,
} from "../../../../types/security_types";
import AccessZoneForm from "./AccessZoneForm";
import images from "../../../../assets/images";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const AccessZones: React.FC = () => {
  const [zones, setZones] = useState<AccessZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] =
    useState<string>("ALL Premises");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState<AccessZone | null>(null);

  useEffect(() => {
    fetchZones();
  }, [searchQuery, selectedRegion, selectedPremises]);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const filters: SecuritySearchFilters = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises")
        filters.premise = selectedPremises;
      if (searchQuery.trim()) filters.searchQuery = searchQuery;

      const data = await getAllAccessZones(filters);
      setZones(data);
    } catch (error: any) {
      console.error("Error fetching access zones:", error);
      toast.error(
        error.response?.data?.resMessage || "Failed to load access zones",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedZone(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (zone: AccessZone) => {
    setSelectedZone(zone);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (zone: AccessZone) => {
    if (
      !window.confirm(`Are you sure you want to delete "${zone.zoneName}"?`)
    ) {
      return;
    }

    try {
      const id = zone._id || zone.id;
      if (!id) return;
      await deleteAccessZone(id);
      toast.success("Access zone deleted successfully");
      fetchZones();
    } catch (error: any) {
      console.error("Error deleting access zone:", error);
      toast.error(
        error.response?.data?.resMessage || "Failed to delete access zone",
      );
    }
  };

  const handleView = (zone: AccessZone) => {
    const id = zone._id || zone.id;
    setSelectedZone(zone);
    setIsViewModalOpen(true);
  };

  const handleFormSuccess = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedZone(null);
    fetchZones();
  };

  const filteredZones = useMemo(() => {
    let filtered = zones;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (zone) =>
          zone.zoneName?.toLowerCase().includes(query) ||
          zone.zoneCode?.toLowerCase().includes(query) ||
          zone.premise?.toLowerCase().includes(query),
      );
    }
    return filtered;
  }, [zones, searchQuery]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "zoneName",
        headerName: "Zone Name",
        sortable: true,
        minWidth: 200,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "zoneCode",
        headerName: "Zone Code",
        sortable: true,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "premise",
        headerName: "Premise",
        sortable: true,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "accessLevel",
        headerName: "Access Level",
        sortable: true,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const level = params.value;
          const colors: Record<string, string> = {
            Public: "bg-green-100 text-green-800",
            Restricted: "bg-yellow-100 text-yellow-800",
            Critical: "bg-red-100 text-red-800",
            PUB: "bg-green-100 text-green-800",
            RES: "bg-yellow-100 text-yellow-800",
            CRT: "bg-red-100 text-red-800",
          };
          const labels: Record<string, string> = {
            Public: "Public",
            Restricted: "Restricted",
            Critical: "Critical",
            PUB: "Public",
            RES: "Restricted",
            CRT: "Critical",
          };
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[level] || "bg-gray-100 text-gray-800"
                }`}
            >
              {labels[level] || level}
            </span>
          );
        },
      },
      {
        field: "allowedRoles",
        headerName: "Allowed Roles",
        sortable: false,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) =>
          `${params.data?.allowedRoles?.length || 0} role(s)`,
      },
      {
        field: "active",
        headerName: "Status",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const isActive = params.value;
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold ${isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
                }`}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        minWidth: 180,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-2 h-full">
              <button
                onClick={() => handleView(params.data)}
                className="text-[#0fa9a6] hover:text-[#008f8c] p-1 rounded transition-colors"
                title="View"
              >
                <img src={images.viewIcon} className="iconTd" alt="View" />
              </button>
              <button
                onClick={() => handleEdit(params.data)}
                className="text-[#0fa9a6] hover:text-[#008f8c] p-1 rounded transition-colors"
                title="Edit"
              >
                <img src={images.editIcon} className="iconTd" alt="Edit" />
              </button>
              <button
                onClick={() => handleDelete(params.data)}
                className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    [],
  );

  const theme = useMemo(() => gridTheme, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <Shield className="w-6 h-6 text-[#0fa9a6]" />
            </div>
            <div>
              <h2 className="ml-2">Access Zones</h2>
              <p className="text-sm text-gray-600 ml-2">
                Manage access zones and their configurations
              </p>
            </div>
          </div>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={handleCreate}
          >
            <Plus size={20} /> Create Zone
          </button>
        </div>

        {/* Filters */}
        <div className="mb-4 flex gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search zones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] text-sm"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {filteredZones?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={filteredZones}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={10}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <Shield className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-gray-500 text-lg mb-4">
                  No access zones found
                </p>
                <p className="text-sm text-gray-400">
                  {searchQuery || selectedRegion !== "ALL Regions"
                    ? "Try adjusting your search or filters"
                    : "Create your first access zone"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedZone(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <AccessZoneForm
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsCreateModalOpen(false);
              setSelectedZone(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsCreateModalOpen(false);
              setSelectedZone(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="accessZoneForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Create
          </button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedZone(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <AccessZoneForm
            zone={selectedZone || undefined}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedZone(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsEditModalOpen(false);
              setSelectedZone(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="accessZoneForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Update
          </button>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedZone(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <AccessZoneForm
            zone={selectedZone || undefined}
            isView={true}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsViewModalOpen(false);
              setSelectedZone(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsViewModalOpen(false);
              setSelectedZone(null);
            }}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};

export default AccessZones;
